/**
 * Data surfaces — section cards, filters, tables, list rows, empty states.
 * Plain objects for inline `style={}`; no blur. Layer on `overlayLayers` only where needed.
 */

import { foundation as f } from './tokens';
import { components } from './theme/componentTokens';
import { semantic as semanticTokens } from './theme/semanticTokens';

const { color, spacePx, radii, shadow, fontSize, fontWeight, lineHeight, fontFamily } = f;
const cardTok = components.card;

/** Default raised panel (sections, grouped content) */
export const dataPanel = {
  backgroundColor: cardTok.background,
  borderRadius: cardTok.radius,
  border: cardTok.border,
  boxShadow: cardTok.shadow,
  boxSizing: 'border-box',
};

/**
 * Tile / stage / KPI box that sits INSIDE a card (RD-UI-079): white, slate-100
 * hairline, radii.md, no shadow. Top-level cards use `dataPanel`; never put a
 * `dataPanel` inside another card.
 */
export const nestedTile = {
  backgroundColor: cardTok.background,
  borderRadius: radii.md,
  border: `1px solid ${semanticTokens.border.subtle}`,
  boxSizing: 'border-box',
};

/** Padded section card (reports, settings blocks) */
export const sectionCard = {
  ...dataPanel,
  padding: spacePx[6],
  marginBottom: spacePx[5],
};

/** Compact card for mobile stacks */
export const mobileSectionCard = {
  ...dataPanel,
  padding: spacePx[4],
  marginBottom: spacePx[4],
};

/** KPI / summary tiles */
export const summaryCard = {
  ...dataPanel,
  padding: `${spacePx[4]} ${spacePx[5]}`,
};

/** Filter toolbar strip (horizontal chips + inputs) */
export const filterBarSurface = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: spacePx[2],
  boxSizing: 'border-box',
};

/** Table + list outer frame */
export const tableShell = {
  width: '100%',
  borderRadius: radii.lg,
  border: `1px solid ${color.neutral.border}`,
  backgroundColor: color.neutral.surface,
  overflow: 'hidden',
  boxSizing: 'border-box',
};

export const table = {
  width: '100%',
  borderCollapse: 'collapse',
  fontFamily: fontFamily.sans,
};

export const tableHeaderCell = {
  backgroundColor: color.neutral.surfaceMuted,
  color: color.neutral.text,
  padding: `${spacePx[3]} ${spacePx[4]}`,
  textAlign: 'left',
  fontSize: fontSize.sm,
  fontWeight: fontWeight.semibold,
  borderBottom: `1px solid ${color.neutral.border}`,
};

export const tableBodyCell = {
  padding: `${spacePx[3]} ${spacePx[4]}`,
  color: color.neutral.text,
  fontSize: fontSize.sm,
  lineHeight: lineHeight.relaxed,
  borderBottom: `1px solid ${color.neutral.border}`,
};

export const tableRow = {
  backgroundColor: color.neutral.surface,
  transition: 'background-color 0.15s cubic-bezier(0.16, 1, 0.3, 1)',
};

/** Apply on hover (inline handlers) or selected row */
export const tableRowHoverBg = color.neutral.surfaceMuted;
export const tableRowSelectedBg = color.brand.primaryMuted;
export const tableRowMutedStripeBg = color.semantic.warningSoft;

export const mobileDataCard = {
  backgroundColor: color.neutral.surface,
  borderRadius: radii.md,
  border: `1px solid ${color.neutral.border}`,
  padding: spacePx[3],
  marginBottom: spacePx[2],
  boxShadow: shadow.xs,
  boxSizing: 'border-box',
};

export const emptyState = {
  textAlign: 'center',
  padding: `${spacePx[8]} ${spacePx[4]}`,
  color: color.neutral.textMuted,
  fontSize: fontSize.md,
  lineHeight: lineHeight.relaxed,
  fontFamily: fontFamily.sans,
};

export const emptyStateTitle = {
  margin: `0 0 ${spacePx[2]} 0`,
  color: color.neutral.text,
  fontSize: fontSize.lg,
  fontWeight: fontWeight.semibold,
  fontFamily: fontFamily.sans,
};

/** Sticky shell sub-bar (tabs under header) — solid, no blur */
export const stickySubBar = {
  backgroundColor: color.neutral.bg,
};

/** App header bar — solid surface */
export const shellHeaderBar = {
  backgroundColor: components.shell.headerBackground,
  backdropFilter: 'none',
};

export const filterBar = filterBarSurface;

/** Responsive flex row for a section title + adjacent action button */
export const sectionHeaderRow = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: spacePx[4],
  marginBottom: spacePx[4],
};

/** Large bold section heading typography */
export const sectionHeading = {
  margin: 0,
  fontSize: fontSize.xl,
  fontWeight: fontWeight.bold,
  color: color.neutral.text,
  letterSpacing: '-0.02em',
  fontFamily: fontFamily.sans,
};

/** CSS grid row for filter inputs — collapses to single column on mobile */
export const filterRow = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: spacePx[2],
  alignItems: 'stretch',
  width: '100%',
};

/** Flex-wrap secondary filter row (below primary filterRow) */
export const filterRowSecondary = {
  display: 'flex',
  gap: spacePx[2],
  alignItems: 'center',
  marginTop: spacePx[2],
  flexWrap: 'wrap',
};

/** Flex row grouping two date inputs with a text separator */
export const dateRangeGroup = {
  display: 'flex',
  alignItems: 'center',
  gap: spacePx[2],
  flexWrap: 'nowrap',
};

/** Flex column wrapper for a list of mobile data cards */
export const mobileCardList = {
  display: 'flex',
  flexDirection: 'column',
  gap: spacePx[2],
  padding: `${spacePx[1]} ${spacePx[1]} ${spacePx[2]}`,
};

/** Canonical invoice/job status → badge style map */
export const STATUS_BADGE_MAP = {
  draft: { backgroundColor: color.neutral.surfaceMuted, color: color.neutral.textMuted, border: `1px solid ${color.neutral.border}` },
  // `sending` is a REAL status on both invoices_status_check and
  // estimates_status_check — it is not a UI-only state. Without an entry here a
  // mid-send document fell through to the grey `draft` treatment, so a document
  // being emailed (or one stuck mid-send) looked unsent. Info tone matches the
  // invoice + estimate token maps, which is what the list rows already render.
  sending: { backgroundColor: color.semantic.infoSoft, color: color.semantic.info, border: `1px solid ${color.semantic.infoBorder}` },
  sent: { backgroundColor: color.semantic.infoSoft, color: color.semantic.info, border: `1px solid ${color.semantic.infoBorder}` },
  paid: { backgroundColor: color.semantic.successSoft, color: color.semantic.success, border: `1px solid ${color.semantic.successBorder}` },
  completed: { backgroundColor: color.semantic.successSoft, color: color.semantic.success, border: `1px solid ${color.semantic.successBorder}` },
  scheduled: { backgroundColor: color.semantic.infoSoft, color: color.semantic.info, border: `1px solid ${color.semantic.infoBorder}` },
  // Warning/danger TEXT uses Strong (RD-UI-030 contrast). Fills/borders unchanged.
  cancelled: { backgroundColor: color.semantic.dangerSoft, color: color.semantic.dangerStrong, border: `1px solid ${color.semantic.dangerBorder}` },
  overdue: { backgroundColor: color.semantic.warningSoft, color: color.semantic.warningStrong, border: `1px solid ${color.semantic.warningBorder}` },
  'no-service': { backgroundColor: color.semantic.dangerSoft, color: color.semantic.dangerStrong, border: `1px solid ${color.semantic.dangerBorder}` },
  approved: { backgroundColor: color.semantic.successSoft, color: color.semantic.success, border: `1px solid ${color.semantic.successBorder}` },
  denied: { backgroundColor: color.semantic.dangerSoft, color: color.semantic.dangerStrong, border: `1px solid ${color.semantic.dangerBorder}` },
  pending: { backgroundColor: color.semantic.warningSoft, color: color.semantic.warningStrong, border: `1px solid ${color.semantic.warningBorder}` },
  // TEAL, not green. Green is reserved for ONE meaning on a money screen —
  // "the money arrived" (estimate fully paid, invoice paid). `accepted` means
  // the customer said yes and nothing has been billed yet, which is a different
  // fact; rendering both in green put two very different states in one colour,
  // the same failure as the amber voided-vs-overdue collision.
  //
  // The teal ramp is shared with the `operations` ROLE tint (semanticTokens
  // exposes it under that name). Deliberate: an operations-role chip and an
  // estimate status pill never appear in the same context, and inventing a
  // second token for the identical hex would just be two names to drift apart.
  accepted: { backgroundColor: color.semantic.operationsSoft, color: color.semantic.operations, border: `1px solid ${color.semantic.operationsBorder}` },
  partially_invoiced: { backgroundColor: color.semantic.warningSoft, color: color.semantic.warningStrong, border: `1px solid ${color.semantic.warningBorder}` },
  invoiced: { backgroundColor: color.semantic.infoSoft, color: color.semantic.info, border: `1px solid ${color.semantic.infoBorder}` },
  declined: { backgroundColor: color.semantic.dangerSoft, color: color.semantic.dangerStrong, border: `1px solid ${color.semantic.dangerBorder}` },
  expired: { backgroundColor: color.neutral.surfaceMuted, color: color.neutral.textMuted, border: `1px solid ${color.neutral.border}` },
  voided: { backgroundColor: color.semantic.dangerSoft, color: color.semantic.dangerStrong, border: `1px solid ${color.semantic.dangerBorder}` },
};

/** Helper: returns badge style for a status key, falls back to neutral */
export function getStatusBadgeStyle(status) {
  return STATUS_BADGE_MAP[status] || { backgroundColor: color.neutral.surfaceMuted, color: color.neutral.textMuted, border: `1px solid ${color.neutral.border}` };
}
